/**
 * ExpandableSection — the ▶ Click to view... component
 *
 * Core UI pattern for the short debriefing guide. Renders a blue
 * italic underlined link with arrow that toggles an inline accordion.
 */

import { type ReactNode, useId } from 'react';

interface ExpandableSectionProps {
  /** Display text (e.g., "Click to view suggested facilitator prompts") */
  label: string;
  /** Whether the section is currently expanded */
  expanded: boolean;
  /** Toggle callback */
  onToggle: () => void;
  /** Content shown when expanded */
  children: ReactNode;
}

export default function ExpandableSection({
  label,
  expanded,
  onToggle,
  children,
}: ExpandableSectionProps) {
  const regionId = useId();
  const buttonId = useId();

  return (
    <div>
      <button
        id={buttonId}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={regionId}
        className="flex items-center gap-2 py-2 min-h-[44px] text-left group"
      >
        <svg
          className={`w-3 h-3 shrink-0 text-[#0563C1] transition-transform duration-200 ${
            expanded ? 'rotate-90' : ''
          }`}
          fill="currentColor"
          viewBox="0 0 8 12"
        >
          <path d="M0 0 L8 6 L0 12 Z" />
        </svg>
        <span className="text-[#0563C1] italic underline text-sm group-hover:text-[#044a9e]">
          {label}
        </span>
      </button>

      {expanded && (
        <div
          id={regionId}
          role="region"
          aria-labelledby={buttonId}
          className="ml-5 pb-3 animate-in fade-in slide-in-from-top-1 duration-200"
        >
          {children}
        </div>
      )}
    </div>
  );
}
